'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/router';
import { AiOutlineMail, AiOutlineUnlock } from 'react-icons/ai';
import { BsPerson } from 'react-icons/bs';
import { RiLockPasswordLine } from 'react-icons/ri';
import Link from 'next/link';
import Image from 'next/image';
import { registerUser } from '../../lib/registerUser';
import toast from 'react-hot-toast';

interface InputErrors {
  [key: string]: string;
}

const SignupForm = () => {
  const [data, setData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(false);

  const [validationErrors, setValidationErrors] = useState<InputErrors>({});
  const [loading, setLoading] = useState(false);

  const router = useRouter();
  const isLt = false;
  const termsHref = isLt ? '/lt/sub/naudojimo-taisykles' : '/sub/terms-of-service';
  const homeHref = isLt ? '/lt/' : '/';
  const rawRedirect = Array.isArray(router.query.redirect)
    ? router.query.redirect[0]
    : router.query.redirect;
  const safeRedirect =
    typeof rawRedirect === 'string' && /^\/(?!\/)/.test(rawRedirect)
      ? rawRedirect
      : '';
  const redirectQuery = safeRedirect
    ? `?redirect=${encodeURIComponent(safeRedirect)}`
    : '';
  const loginHref = (isLt ? '/lt/prisijungti' : '/login') + redirectQuery;

  const validateData = () => {
    const errors: InputErrors = {};
    const cleaned = {
      fullName: data.fullName.trim(),
      email: data.email.trim(),
      password: data.password.trim(),
      confirmPassword: data.confirmPassword.trim(),
    };

    if (cleaned.fullName.length < 4) {
      errors.fullName = isLt ? 'Vardas turi būti bent 4 simbolių' : 'Full name must be at least 4 characters';
    } else if (cleaned.fullName.length > 30) {
      errors.fullName = isLt ? 'Vardas turi būti trumpesnis nei 30 simbolių' : 'Full name should be less than 30 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(cleaned.fullName)) {
      errors.fullName = isLt ? 'Leidžiamos tik raidės ir tarpai' : 'Only letters and spaces allowed';
    }

    if (!/\S+@\S+\.\S+/.test(cleaned.email)) {
      errors.email = isLt ? 'Neteisingas el. pašto formatas' : 'Invalid email format';
    }

    if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,}$/.test(cleaned.password)) {
      errors.password =
        isLt
          ? 'Slaptažodis turi būti bent 6 simbolių ir sudarytas iš raidžių bei skaičių'
          : 'Password must be at least 6 characters and contain letters and numbers';
    }

    if (cleaned.password !== cleaned.confirmPassword) {
      errors.confirmPassword = isLt ? 'Slaptažodžiai nesutampa' : "Passwords don't match";
    }

    if (!agreedToTerms) {
      errors.terms = isLt ? 'Turite sutikti su naudojimo taisyklėmis' : 'You must agree to the terms of service';
    }

    setValidationErrors(errors);

    // show first error in toast
    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      toast.error(firstError);
    }

    return Object.keys(errors).length === 0;
  };

  const handleSignup = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateData()) return;

    try {
      setLoading(true);
      const locale: 'en' | 'lt' = 'en';
      await registerUser(
        data.fullName.trim(),
        data.email.trim(),
        data.password.trim(),
        process.env.NEXT_PUBLIC_CLIENT_ID || 'default',
        locale
      );

      // Subscribe to newsletter if opted in
      if (subscribeNewsletter) {
        fetch('/api/newsletter/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: data.email.trim(), source: 'signup' }),
        }).catch(() => {});
      }

      toast.success(isLt ? 'Patikrinkite paštą, kad patvirtintumėte el. paštą' : 'Check your inbox to verify your email');
      
      // Redirect to login page after successful registration
      setTimeout(() => {
        router.push(loginHref);
      }, 2000);
    } catch (err: any) {
      const errorCode = err?.code || '';
      const apiError = err?.response?.data?.error || err?.response?.data?.details;
      let errorMessage = '';

      if (errorCode === 'auth/email-already-in-use') {
        errorMessage = isLt 
          ? 'Šis el. paštas jau užregistruotas. Prašome prisijungti.'
          : 'This email is already registered. Please login instead.';
      } else if (errorCode === 'auth/weak-password') {
        errorMessage = isLt
          ? 'Slaptažodis per silpnas. Naudokite bent 6 simbolius.'
          : 'Password is too weak. Use at least 6 characters.';
      } else if (errorCode === 'auth/invalid-email') {
        errorMessage = isLt
          ? 'Neteisingas el. pašto formatas.'
          : 'Invalid email format.';
      } else if (apiError) {
        errorMessage = typeof apiError === 'string' ? apiError : err?.message || (isLt ? 'Registracija nepavyko' : 'Signup failed');
      } else {
        errorMessage = err?.message || (isLt ? 'Registracija nepavyko' : 'Signup failed');
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  return (
    <section className='flex items-start justify-center w-full px-4 pt-10'>
      <div className='w-full max-w-md bg-white/95 dark:bg-black border border-gray-200 dark:border-gray-800 rounded-xl px-6 pt-4 pb-6'>
        <Link href={homeHref} className='mb-6 mt-6 flex justify-center'>
          <Image src='/GadgetFever/GadgetFever_webp_logo.webp' alt='GadgetFever' width={220} height={70} className='object-contain' />
        </Link>

        <h1 className='text-2xl font-semibold text-center mt-1 mb-1 text-gray-900 dark:text-white'>
          {isLt ? 'Sukurti paskyrą' : 'Create Account'}
        </h1>
        <p className='text-center text-sm text-gray-600 dark:text-gray-400 mb-4'>
          {isLt ? 'Užpildykite duomenis žemiau' : 'Fill in your details below'}
        </p>

        <form className='space-y-4' onSubmit={handleSignup}>
          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>{isLt ? 'Vardas' : 'Full Name'}</label>
            <div className='flex items-center gap-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-black focus-within:ring-1 focus-within:ring-gray-600'>
              <BsPerson className='text-gray-500 dark:text-gray-400 flex-shrink-0 ml-3' />
              <input
              type='text'
              name='fullName'
              placeholder={isLt ? 'Vardas Pavardė' : 'John Doe'}
              value={data.fullName}
              onChange={handleInputChange}
              className='flex-1 min-w-0 py-3 pr-4 bg-transparent text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none'
            />
            </div>
          </div>
          {validationErrors.fullName && <p className='text-red-500 text-sm'>{validationErrors.fullName}</p>}

          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>{isLt ? 'El. paštas' : 'Email'}</label>
            <div className='flex items-center gap-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-black focus-within:ring-1 focus-within:ring-gray-600'>
              <AiOutlineMail className='text-gray-500 dark:text-gray-400 flex-shrink-0 ml-3' />
              <input
                type='email'
                name='email'
                placeholder='you@example.com'
                value={data.email}
                onChange={handleInputChange}
                className='flex-1 min-w-0 py-3 pr-4 bg-transparent text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none'
              />
            </div>
          </div>
          {validationErrors.email && <p className='text-red-500 text-sm'>{validationErrors.email}</p>}

          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>{isLt ? 'Slaptažodis' : 'Password'}</label>
            <div className='flex items-center gap-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-black focus-within:ring-1 focus-within:ring-gray-600'>
              <AiOutlineUnlock className='text-gray-500 dark:text-gray-400 flex-shrink-0 ml-3' />
              <input
                type='password'
                name='password'
                placeholder='••••••••'
                value={data.password}
                onChange={handleInputChange}
                className='flex-1 min-w-0 py-3 pr-4 bg-transparent text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none'
              />
            </div>
          </div>
          {validationErrors.password && <p className='text-red-500 text-sm'>{validationErrors.password}</p>}

          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>{isLt ? 'Patvirtinti slaptažodį' : 'Confirm Password'}</label>
            <div className='flex items-center gap-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-black focus-within:ring-1 focus-within:ring-gray-600'>
              <RiLockPasswordLine className='text-gray-500 dark:text-gray-400 flex-shrink-0 ml-3' />
              <input
                type='password'
                name='confirmPassword'
                placeholder='••••••••'
                value={data.confirmPassword}
                onChange={handleInputChange}
                className='flex-1 min-w-0 py-3 pr-4 bg-transparent text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none'
              />
            </div>
          </div>
          {validationErrors.confirmPassword && <p className='text-red-500 text-sm'>{validationErrors.confirmPassword}</p>}

          <label className='flex items-start gap-2 cursor-pointer group'>
            <input type='checkbox' checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} className='mt-1 rounded border-gray-400 text-primary focus:ring-primary' />
            <span className='text-xs text-gray-600 dark:text-gray-400 group-hover:text-gray-800 dark:group-hover:text-gray-200'>
              {isLt ? 'Sutinku su ' : 'I agree to the '}
              <Link href={termsHref} target='_blank' rel='noopener noreferrer' className='text-gray-900 dark:text-white underline hover:no-underline'>
                {isLt ? 'naudojimo taisyklėmis' : 'terms of service'}
              </Link>
              .
            </span>
          </label>
          {validationErrors.terms && <p className='text-red-500 text-sm'>{validationErrors.terms}</p>}

          <label className='flex items-start gap-2 cursor-pointer group'>
            <input type='checkbox' checked={subscribeNewsletter} onChange={(e) => setSubscribeNewsletter(e.target.checked)} className='mt-1 rounded border-gray-400 text-primary focus:ring-primary' />
            <span className='text-xs text-gray-600 dark:text-gray-400 group-hover:text-gray-800 dark:group-hover:text-gray-200'>
              {isLt ? 'Norėčiau gauti naujienlaiškius ir geriausius pasiūlymus.' : 'I want to subscribe to the newsletter and get the best deals.'}
            </span>
          </label>

          <button
            type='submit'
            disabled={loading}
            className='w-full bg-black hover:bg-gray-900 dark:hover:bg-[#090909] disabled:opacity-60 text-white font-semibold py-3 rounded-md transition-colors border border-gray-700'
          >
            {loading ? (isLt ? 'Vykdoma...' : 'Processing...') : isLt ? 'Registruotis' : 'Sign Up'}
          </button>

          <Link href={homeHref} className='block w-full text-center py-2.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors'>
            {isLt ? '← Į pradžios puslapį' : '← Back to home'}
          </Link>

          <p className='text-sm text-center text-gray-600 dark:text-gray-400'>
            {isLt ? 'Jau turite paskyrą?' : 'Already have an account?'}{' '}
            <Link href={loginHref} className='text-gray-900 dark:text-white font-medium hover:underline'>
              {isLt ? 'Prisijunkite' : 'Log in'}
            </Link>
          </p>

        </form>
      </div>
    </section>
  );
};

export default SignupForm;
